import { Component, computed, DestroyRef, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserProfileActionsService } from '@app/shared/services/actions/user-profile-actions.service';
import { AccountLanguageCode } from '@app/shared/types/models/user/user-profile.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { ProffeoUiSelectOption } from '@app/shared/ui/types/select.types';
import { TranslatePipe } from '@ngx-translate/core';

/** Account settings section (account language), built from existing DS controls. */
@Component({
  selector: 'proffeo-user-profile-settings',
  templateUrl: './user-profile-settings.component.html',
  styleUrl: './user-profile-settings.component.css',
  imports: [UiButtonComponent, UiSelectComponent, TranslatePipe]
})
export class UserProfileSettingsComponent {
  private readonly userProfileActions = inject(UserProfileActionsService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly languageOptions: ProffeoUiSelectOption<AccountLanguageCode>[] = [
    { value: 'PL', label: 'user-profile-settings.language-option-pl' },
    { value: 'EN', label: 'user-profile-settings.language-option-en' },
    { value: 'NO', label: 'user-profile-settings.language-option-no' }
  ];
  protected readonly accountLanguage = this.userProfileActions.getAccountLanguage();
  protected readonly profileState = this.userProfileActions.getOwnProfileState();
  protected readonly saveError = signal(false);
  protected readonly saveSucceeded = signal(false);
  // Null means no local draft: refreshes can update the displayed server value without becoming dirty.
  protected readonly draftLanguage = signal<AccountLanguageCode | null>(null);
  protected readonly effectiveDraft = computed(() => this.draftLanguage() ?? this.accountLanguage() ?? null);
  public readonly saving = this.userProfileActions.isPreferencesSaving();
  public readonly isDirty = computed(
    () => this.draftLanguage() !== null && this.draftLanguage() !== this.accountLanguage()
  );

  public constructor() {
    this.userProfileActions.loadOwnProfile();
  }

  @HostListener('window:beforeunload', ['$event'])
  protected preventUnsavedClose(event: BeforeUnloadEvent): void {
    if (this.isDirty() || this.saving()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  public discardChanges(): void {
    if (this.saving()) return;
    this.draftLanguage.set(null);
    this.saveError.set(false);
    this.saveSucceeded.set(false);
  }

  protected onDraftChange(value: AccountLanguageCode | AccountLanguageCode[] | null): void {
    if (Array.isArray(value) || this.saving()) return;
    this.draftLanguage.set(value);
    this.saveError.set(false);
    this.saveSucceeded.set(false);
  }

  protected retryLoad(): void {
    this.userProfileActions.refreshOwnProfile();
  }

  protected save(): void {
    const draft = this.draftLanguage();
    if (draft === null || !this.isDirty() || this.saving() || !this.profileState().loaded) return;
    this.saveError.set(false);
    this.saveSucceeded.set(false);
    this.userProfileActions
      .updateAccountLanguage(draft)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.draftLanguage.set(null);
          this.saveSucceeded.set(true);
        },
        error: () => this.saveError.set(true)
      });
  }
}
